import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, MailCheck, Send } from "lucide-react";
import { api, extractErr } from "@/portal/PortalAuth";
import { btn, ghost, card, inp } from "@/portal/PortalShell";

export default function EmailSenderSettings() {
  const [s, setS] = useState(null);
  const [form, setForm] = useState({ from_name: "", reply_to: "", payment_emails_enabled: true, admin_reminder_emails_enabled: true, payment_reminders_enabled: false });
  const [busy, setBusy] = useState("");
  const load = async () => {
    try {
      const { data } = await api.get("/admin/email-settings");
      setS(data);
      setForm({ from_name: data.from_name || "", reply_to: data.reply_to || "", payment_emails_enabled: data.payment_emails_enabled !== false, admin_reminder_emails_enabled: data.admin_reminder_emails_enabled !== false, payment_reminders_enabled: data.payment_reminders_enabled === true });
    } catch (e) { toast.error(extractErr(e)); }
  };
  useEffect(() => { load(); }, []);
  const save = async (e) => {
    e.preventDefault();
    setBusy("save");
    try { const { data } = await api.put("/admin/email-settings", form); setS(data); toast.success("Sender settings saved"); }
    catch (err) { toast.error(extractErr(err)); } finally { setBusy(""); }
  };
  const test = async () => {
    setBusy("test");
    try { const { data } = await api.post("/admin/email-settings/test"); toast.success(`Sample payment confirmation sent to ${data.sent_to}`); await load(); }
    catch (err) { toast.error(extractErr(err)); } finally { setBusy(""); }
  };
  if (!s) return null;
  return (
    <form onSubmit={save} className={card} data-testid="email-sender-card">
      <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#8A9A86]">Guest payment emails</p>
      <h3 className="mt-1 text-lg font-normal text-[#1C1917]">Sender identity for payment & refund confirmations</h3>
      <p className="mt-1 text-sm text-[#78716C]">
        Branded confirmations go to the guest only after Stripe verifies a successful payment, and refund notices only after Stripe confirms the refund.
        The From address is managed by the email platform; you control the display name and the Reply-To inbox. Current source: {s.source}.
      </p>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="text-xs text-[#78716C]">Sender display name
          <input value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })} data-testid="email-from-name" className={inp + " mt-1"} placeholder="Masaya Hospitality" />
        </label>
        <label className="text-xs text-[#78716C]">Reply-To email (your verified business inbox)
          <input type="email" value={form.reply_to} onChange={(e) => setForm({ ...form, reply_to: e.target.value })} data-testid="email-reply-to" className={inp + " mt-1"} placeholder="hello@masayahospitality.com" />
        </label>
      </div>
      <div className="mt-4 rounded-lg border border-[#E7E5E4] bg-[#FAFAF9] p-3">
        <label className="flex items-center gap-2 text-xs font-medium text-[#1C1917]">
          <input type="checkbox" checked={form.payment_reminders_enabled} onChange={(e) => setForm({ ...form, payment_reminders_enabled: e.target.checked })} data-testid="email-reminders-engine-enabled" />
          Turn on the daily payment-reminder engine
        </label>
        <p className="mt-1 pl-6 text-[11px] leading-relaxed text-[#78716C]">
          Master switch. While off, no payment reminders are sent to anyone (guest or management). Payment and refund
          confirmations are unaffected. Leave off until you're ready to review, then switch on and save.
        </p>
      </div>
      <label className="mt-3 flex items-center gap-2 text-xs text-[#44403C]">
        <input type="checkbox" checked={form.payment_emails_enabled} onChange={(e) => setForm({ ...form, payment_emails_enabled: e.target.checked })} data-testid="email-payment-enabled" />
        Send guest payment, refund and payment-reminder emails
      </label>
      <label className="mt-2 flex items-center gap-2 text-xs text-[#44403C]">
        <input type="checkbox" checked={form.admin_reminder_emails_enabled} onChange={(e) => setForm({ ...form, admin_reminder_emails_enabled: e.target.checked })} data-testid="email-admin-reminder-enabled" />
        Send management alerts for overdue payments and missing payment links (to your notification recipients)
      </label>
      <p className="mt-2 text-[11px] leading-relaxed text-[#78716C]">
        When the reminder engine is on, reminders run daily at 9:00&nbsp;AM America/Chicago: 5 days before, 1 day before,
        on the due date (with a secure Guest Portal link), and 1 day overdue. Guests always pay themselves — no saved card
        is ever charged automatically.
      </p>
      <div className="mt-4 flex flex-wrap gap-2 items-center">
        <button disabled={!!busy} data-testid="email-settings-save" className={btn + " text-xs"}>{busy === "save" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MailCheck className="w-3.5 h-3.5" />} Save sender settings</button>
        <button type="button" onClick={test} disabled={!!busy} data-testid="email-settings-test" className={ghost + " text-xs"}>{busy === "test" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send me a sample confirmation</button>
        {s.last_test && <span data-testid="email-last-test" className="text-xs text-[#78716C]">Last sample sent to {s.last_test.sent_to} · {new Date(s.last_test.at).toLocaleString()}</span>}
      </div>
    </form>
  );
}
